import { useCallback, useEffect, useState } from "react";

import * as Auth from "fond/cognito/auth";
import { CognitoUser, MfaPreference } from "fond/types";

interface UseMfaStatusResult {
  mfaStatus: MfaPreference | null;
  setMfaStatus: (status: MfaPreference) => void;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Hook to check the current user's MFA preference.
 * Returns the MFA status (MFA_NONE or MFA_SOFTWARE_TOKEN), loading state,
 * and a refresh function to re-check after setup.
 */
export const useMfaStatus = (): UseMfaStatusResult => {
  const [mfaStatus, setMfaStatus] = useState<MfaPreference | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMfaStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const user: CognitoUser = await Auth.currentAuthenticatedUser();
      const preferred = await Auth.getPreferredMFA(user);
      setMfaStatus(preferred as MfaPreference);
    } catch {
      setError("Unable to load MFA status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMfaStatus();
  }, [fetchMfaStatus]);

  return { mfaStatus, setMfaStatus, isLoading, error, refresh: fetchMfaStatus };
};
